import { areObjectsDeeplyDifferent } from '@/pages/order/components/productHelpers';
import type { Draft } from 'immer';

const INFINITE_LIMIT_AUTO = Symbol('INFINITE_LIMIT_AUTO');

let LAST_COLLATE = 'C';

export function injectIntoInfiniteData2<
  const T extends API.DbItem,
  const M,
  const ItemType extends API.ExtractFromDraft<API.WithPage<T[], M>>,
  const DraftData extends ItemType | Draft<ItemType>,
>(
  items: T[],
  prevData: ItemType | undefined,
  draftData: DraftData,
  knownIds?: React.MutableRefObject<Set<number>>,
  cursorKeyBase: keyof T | undefined = undefined,
  // @deprecated We used to use this to set pageParams.limit but we don't use it anymore
  _limit: number | typeof INFINITE_LIMIT_AUTO = INFINITE_LIMIT_AUTO,
  injectPreference?: 'start' | 'end' | 'never',
  replaceMode?: 'replace' | 'merge', // | 'deep',
) {
  let modified = false;
  const added: T[] = [];

  if (draftData && !draftData.data?.length && prevData?.data?.length && prevData?.pageInfo) {
    // Initialize draft data with the data from previous
    draftData.pageInfo = prevData.pageInfo;
    draftData.data = prevData.data;
  }

  // Infer the cursorKey if undefined by using createdAt by default, or id if it dosn't have that
  let cursorKey: keyof T = cursorKeyBase || 'id';

  const draftDir = draftData?.pageInfo?.order?.dir;
  const prevDir = prevData?.pageInfo?.order?.dir;
  const draftBy = draftData?.pageInfo?.order?.by;
  const prevBy = prevData?.pageInfo?.order?.by;
  const draftLimit = draftData?.pageInfo?.limit;
  const prevLimit = prevData?.pageInfo?.limit;
  const isDescending = (draftDir || prevDir) === 'DESC';
  const afterCode = isDescending ? 1 : -1;
  const beforeCode = isDescending ? -1 : 1;

  LAST_COLLATE = (draftData?.pageInfo?.coll || prevData?.pageInfo?.coll || LAST_COLLATE) as string;

  // When new data in is in opposite order, we should iterate over it in reverse and inject it in the opposite manner
  const injPref = injectPreference === undefined ? (prevDir ? 'end' : 'start') : injectPreference;
  const reverse = prevDir && draftDir && prevDir !== draftDir;
  const iterator = reverse ? reverseIterator(items) : items;
  const startCursor = reverse ? 'endCursor' : 'startCursor';
  const endCursor = reverse ? 'startCursor' : 'endCursor';
  const startCursorId = reverse ? 'endCursorId' : 'startCursorId';
  const endCursorId = reverse ? 'startCursorId' : 'endCursorId';

  let comparator = (draftBy ||
    prevBy ||
    (!!(draftData?.data?.[0] || items[0])?.createdAt && 'createdAt') ||
    'id') as keyof T;

  const isIteratingInReverse = injPref === 'end';

  itemLoop: for (let newItem of iterator) {
    // In case we didn't have it set before because an item was blank, try again
    cursorKey = cursorKey || cursorKeyBase || 'id';

    // We will refer to this as Id in this code, even though it can be an arbitrary cursor key
    const iId = newItem[cursorKey] as API.Id;

    if (draftData?.data.length) {
      const currentFirstItem = draftData.data[0];
      const currentLastItem = draftData.data[draftData.data.length - 1];
      const isInSet = !!knownIds?.current?.has(iId);

      comparator =
        comparator || (newItem.createdAt && currentLastItem?.createdAt ? 'createdAt' : 'id');

      // If we use a non-unique key (like gameTime), we need a secondary comparison to check if two entries are the same
      const isPossiblyUnique =
        comparator === 'id' || /(?:ID|Id|[u_.-]id)$/.test(comparator as string);
      // If it's an ID or UUID based key, it's likely unique. Otherwise, fall back to id or createdAt
      const iIdU = isPossiblyUnique ? iId : newItem.id || newItem.createdAt || iId;

      // const isProbablyDate =
      //   comparator === 'createdAt' ||
      //   comparator === 'date' ||
      //   comparator === 'timestamp' ||
      //   (comparator as string).endsWith('At') ||
      //   (comparator as string).endsWith('Date') ||
      //   (comparator as string).endsWith('Time') ||
      //   (comparator as string).endsWith('Timestamp');

      const iCompVal = newItem[comparator];

      // const isProbablyNumber = typeof iCompVal === 'number';

      const firstCompVal = currentFirstItem[comparator];
      const lastCompVal = currentLastItem[comparator];

      const iFirstComp = strComp(iCompVal, firstCompVal);
      const iLastComp = strComp(iCompVal, lastCompVal);

      const isSameAsFirst = iFirstComp === 0;
      const isSameAsLast = iLastComp === 0;
      const isGreaterThanFirst = iFirstComp === 1;
      const isGreaterThanLast = iLastComp === 1;
      const isLowerThanFirst = iFirstComp === -1;
      const isLowerThanLast = iLastComp === -1;
      const isBetweenFirstLast = isDescending
        ? (isGreaterThanLast || isSameAsLast) && (isLowerThanFirst || isSameAsFirst)
        : (isGreaterThanFirst || isSameAsFirst) && (isLowerThanLast || isSameAsLast);

      let wasRemoved = false;
      let needToRemove = false;

      if (isInSet || isBetweenFirstLast) {
        // ID is within the range we have already loaded. Replace existing one (prevent doubles), or inject (in-order)
        let injected = false;

        // If the injection preference is at the end, then we go in reverse
        // Useful for "load more" at the bottom
        // const isIteratingInReverse = injPref === 'end' && !isDescending;
        const dataIndexIterator = indexIterator(draftData.data, isIteratingInReverse);

        let iteratorResult = dataIndexIterator.next();

        entryLoop: while (!iteratorResult.done) {
          const entryIndex = iteratorResult.value;

          iteratorResult = dataIndexIterator.next();

          const entry = draftData.data[entryIndex];

          if (!entry) {
            console.warn(`Tried to find "${entryIndex}" of draftData.data, got: ${entry}`);
            continue;
          }

          const eCompVal = entry[comparator];
          const iEntryComp = strComp(iCompVal, eCompVal);
          const eId = entry[cursorKey] as API.Id;
          const eIdU = isPossiblyUnique ? eId : entry.id || entry.createdAt || eId;
          const sameId = iIdU === eIdU;
          // const iBiggerId = iId > eId;
          // const iLowerId = iId < eId;

          if (needToRemove && !wasRemoved) {
            if (sameId) {
              if (replaceMode === 'merge') {
                newItem = { ...entry, ...newItem };
              }

              draftData.data.splice(+entryIndex, 1);

              wasRemoved = true;
              modified = true;

              continue itemLoop;
            }

            if (!injected) {
              continue entryLoop;
            }
          }

          if (injected) {
            // probably looking to remove the old copy
            continue entryLoop;
          }

          if (iEntryComp === 0) {
            // same value, try secondary sort
            if (sameId) {
              // item hasn't changed, we can splice it in place to make sure it has latest data
              wasRemoved = true;
              injected = true;
              needToRemove = false;

              if (areObjectsDeeplyDifferent(newItem, entry)) {
                // only replace if different
                if (replaceMode === 'merge') {
                  newItem = { ...entry, ...newItem };
                }

                draftData.data.splice(+entryIndex, 1, newItem);

                modified = true;

                // We previously used isProbablyDate instead of areObjectsDeeplyDifferent, as it was less expensive
                // But this is overall more logical
                // @todo should we compare updatedAt times?
              }

              continue itemLoop;
            }

            let targetIndex = +entryIndex;

            do {
              if (iteratorResult.done || iteratorResult.value === undefined) {
                continue; // i have no idea how this happens but it does
              }

              const nextIndex = iteratorResult.value;
              const next = draftData.data[nextIndex];
              const nCompVal = next[comparator];
              const iNextComp = strComp(iCompVal, nCompVal);
              const nId = next[cursorKey] as API.Id;
              const nIdU = isPossiblyUnique ? nId : next.id || next.createdAt || nId;
              const sameNextId = iIdU === nIdU;
              const iBiggerThanNextId = iId > nId;
              const iLowerThanNextId = iId < nId;

              if (iNextComp) {
                // next value changed, so this must be the spot
                const removeCount = sameNextId ? 1 : 0;

                if (removeCount) {
                  wasRemoved = true;
                }

                draftData.data.splice(
                  +nextIndex + (isPossiblyUnique ? iNextComp : targetIndex),
                  removeCount,
                  newItem,
                );

                if (!removeCount) {
                  added.push(newItem);
                }

                injected = true;
                modified = true;
                needToRemove = isInSet && !wasRemoved;

                if (!needToRemove) {
                  continue itemLoop;
                } else {
                  continue entryLoop;
                }
              } else {
                // next value is still the same
                if (sameNextId) {
                  // same item, splice it out
                  if (replaceMode === 'merge') {
                    newItem = { ...entry, ...newItem };
                  }

                  draftData.data.splice(+nextIndex, 1, newItem);

                  modified = true;
                  injected = true;
                  wasRemoved = true;
                  needToRemove = isInSet && !wasRemoved;

                  continue itemLoop;
                } else if (isDescending ? iLowerThanNextId : iBiggerThanNextId) {
                  // different item, and ID is now lower so we insert here
                  targetIndex = +nextIndex;
                } else {
                  // injection point using target index
                  draftData.data.splice(
                    isIteratingInReverse ? +targetIndex + 1 : +targetIndex,
                    0,
                    newItem,
                  );

                  if (!wasRemoved) {
                    added.push(newItem);
                  }

                  modified = true;
                  injected = true;
                  needToRemove = isInSet && !wasRemoved;

                  if (needToRemove) {
                    continue entryLoop;
                  } else {
                    continue itemLoop;
                  }
                }
              }

              iteratorResult = dataIndexIterator.next();
            } while (!iteratorResult.done);

            continue entryLoop;
          } else if (iEntryComp === (isIteratingInReverse ? afterCode : beforeCode)) {
            // belongs before this item
            continue entryLoop;
          } else {
            // belongs after this item
            if (isIteratingInReverse && isDescending) {
              draftData.data.splice(+entryIndex + 1, 0, newItem);
            } else {
              draftData.data.splice(+entryIndex, 0, newItem);
            }

            if (!wasRemoved) {
              added.push(newItem);
            }

            modified = true;
            injected = true;
            needToRemove = isInSet && !wasRemoved;
          }

          if (injected && !needToRemove) {
            // we're done with this item
            continue itemLoop;
          }
        }

        if (injected) {
          // We managed to inject this item into our list; move onto the next one
          continue itemLoop;
        }

        if (isInSet && !wasRemoved) {
          // This item was already in the set, but we never removed it, so there's nothing else to do with it
          continue itemLoop;
        }
      }

      // If we end up here, somehow we didn't find a place to inject these.

      if (injectPreference === 'never') {
        continue itemLoop;
      }

      knownIds?.current?.add(iId);

      const shouldInjectAtEnd = isDescending
        ? isLowerThanLast || isSameAsLast
        : isGreaterThanLast || isSameAsLast;

      if (shouldInjectAtEnd) {
        // This is older than the Last item; inject it at the end
        draftData.data.push(newItem);
        draftData.pageInfo[endCursor] = newItem[cursorKey] as API.Id;

        if (newItem.id) {
          draftData.pageInfo[endCursorId] = newItem.id as API.Id;
        }
      } else {
        // Otherwise, inject it as the First item
        draftData.data.unshift(newItem);
        draftData.pageInfo[startCursor] = newItem[cursorKey] as API.Id;

        if (newItem.id) {
          draftData.pageInfo[startCursorId] = newItem.id as API.Id;
        }
      }

      added.push(newItem);

      modified = true;

      continue itemLoop;
    }

    // We didn't have any pages, so add it on top.

    draftData.data = [newItem];
    draftData.pageInfo = {
      endCursor: newItem[cursorKey] as API.Id,
      startCursor: newItem[cursorKey] as API.Id,
      hasNextPage: false,
      limit: draftLimit || prevLimit || 1,
      order: { by: comparator || 'id', dir: draftDir || prevDir || 'DESC' },
      // Intentionally NO `total`: this bootstrap fires only for the FIRST item
      // injected into an empty draft (e.g. after a `clear` refetch re-injects N
      // rows). A hardcoded `total: 1` is wrong the instant a second row appends —
      // the append path never revises it — so a consumer reading `pageInfo.total`
      // (usePendingPicksCount) would see 1 for an N-row set. The real total is the
      // server's, applied by the caller from `json.pageInfo.total` when present
      // (useInfiniteActions `totalChanged`); leave it unknown here otherwise so
      // callers correctly fall back to `data.length`.
    };

    if (newItem.id) {
      draftData.pageInfo[startCursorId] = newItem.id as API.Id;
      draftData.pageInfo[endCursorId] = newItem.id as API.Id;
    }

    added.push(newItem);

    knownIds?.current?.add(iId);

    modified = true;
  }

  return modified ? added : null;
}

/** Simple helper to reverse iterating over an array without having to run `array.reverse()` */
function* reverseIterator<T>(array: T[]): IterableIterator<T> {
  let index = array?.length || 0;

  while (index > 0) {
    index--;
    yield array[index];
  }
}

function* indexIterator<T>(array: T[], reverse: boolean = false): IterableIterator<number> {
  if (reverse) {
    let index = array?.length || 0;

    while (index > 0) {
      index--;
      yield index;
    }
  } else {
    let index = 0;

    while (index < (array?.length || 0)) {
      yield index;
      index++;
    }
  }
}

type Collators = Record<'normal' | 'ignorePunctuation' | 'numeric', Intl.Collator>;

const collators = new Map<string, Collators>();

/**
 * This was created because in dev, the Docker container only has C collation.
 * In prod, it has en_US.utf8. This means sort orders are different.
 */
function strComp(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  val1: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  val2: any,
  collation: 'C' | string = LAST_COLLATE,
  method: keyof Collators | 'auto' = 'auto',
): 0 | 1 | -1 {
  if (collation === 'C') {
    return val1 === val2 ? 0 : val1 > val2 ? 1 : -1;
  }

  let collator = collators.get(collation);

  if (!collator) {
    collator = {
      normal: new Intl.Collator(collation, {
        sensitivity: 'base',
        numeric: false,
        ignorePunctuation: false,
      }),
      ignorePunctuation: new Intl.Collator(collation, {
        sensitivity: 'base',
        numeric: false,
        ignorePunctuation: true,
      }),
      numeric: new Intl.Collator(collation, {
        sensitivity: 'base',
        numeric: true,
        ignorePunctuation: true,
      }),
    };

    collators.set(collation, collator);
  }

  if (method === 'auto') {
    const tv1 = val1 instanceof Date ? 'date' : typeof val1;
    const tv2 = val2 instanceof Date ? 'date' : typeof val2;
    const isNumeric =
      (tv1 === 'number' ||
        tv2 === 'number' ||
        tv1 === 'bigint' ||
        tv2 === 'bigint' ||
        tv1 === 'date' ||
        tv2 === 'date') &&
      (tv1 === tv2 || !tv1 || !tv2);

    if (isNumeric) {
      return collator.numeric.compare(val1, val2) as 0 | 1 | -1;
    }

    // A bit more like postgres en_US.utf8. Handles strings starting with spaces.
    const ignoredPunctuation = collator.ignorePunctuation.compare(val1, val2) as 0 | 1 | -1;

    if (ignoredPunctuation === 0) {
      return collator.normal.compare(val1, val2) as 0 | 1 | -1;
    }

    return ignoredPunctuation;
  }

  if (collator?.[method]) {
    return collator[method].compare(val1, val2) as 0 | 1 | -1;
  }

  return 0;
}
